import React, { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { T, useTheme } from "Components";

import useStatistics from "../../Infrastructure/useStatistics";
import { CHART_FONT, ReadChartPalette, TooltipStyle } from "../../Infrastructure/chartSetup";
import { FormatNumber, FormatPercent } from "../../Infrastructure/format";

/**
 * Hayvan Varlığı Dağılımı — ırk bazında halka grafik.
 * Renkler ilk beş ırk için tasarımdaki sırayla; sonrası kenarlık rengine düşer.
 */
const sliceColors = (palette) => [
  palette.primary,
  palette.teal,
  palette.amber,
  palette.violet,
  palette.border2,
];

const BreedChart = () => {
  const { data, loading, error } = useStatistics("/Statistics/AnimalDistribution", []);
  const { theme } = useTheme();

  const breeds = Array.isArray(data) ? data : [];
  const total = breeds.reduce((sum, item) => sum + item.Count, 0);

  const { chartData, options } = useMemo(() => {
    const palette = ReadChartPalette();
    const colors = sliceColors(palette);

    return {
      chartData: {
        labels: breeds.map((item) => item.Breed),
        datasets: [
          {
            data: breeds.map((item) => item.Count),
            backgroundColor: breeds.map(
              (unused, index) => colors[index] ?? palette.border2,
            ),
            borderColor: palette.surface,
            borderWidth: 2.5,
          },
        ],
      },
      options: {
        responsive: true,
        cutout: "56%",
        radius: "82%",
        plugins: {
          legend: {
            position: "right",
            labels: {
              boxWidth: 9,
              boxHeight: 9,
              padding: 13,
              color: palette.text2,
              font: { family: CHART_FONT, size: 11.5, weight: "600" },
            },
          },
          tooltip: {
            ...TooltipStyle(palette),
            callbacks: {
              label: (context) =>
                ` ${context.label}: ${FormatNumber(context.parsed)} (${FormatPercent(
                  total === 0 ? 0 : (context.parsed / total) * 100,
                )})`,
            },
          },
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breeds, total, theme]);

  return (
    <section className="fdash__card fdash__chart">
      <header className="fdash__card__head">
        <div className="fdash__card__title">
          <b>{T.Main("Dashboard.Statistics.AnimalDistribution")}</b>
          <span>
            {`${T.Main("Dashboard.Statistics.ByBreed")} · ${FormatNumber(total)} ${T.Main(
              "Dashboard.Statistics.HeadOfCattle",
            )}`}
          </span>
        </div>
      </header>

      {error && (
        <div className="fdash__state fdash__state--error">
          {T.Main("Dashboard.Statistics.LoadError")}
        </div>
      )}

      {!error && loading && (
        <div className="fdash__card__body">
          <div className="fdash__skeleton" style={{ height: 250 }} />
        </div>
      )}

      {!error && !loading && breeds.length === 0 && (
        <div className="fdash__state">{T.Main("Dashboard.Statistics.NoData")}</div>
      )}

      {!error && !loading && breeds.length > 0 && (
        <div className="fdash__chart__canvas">
          <Doughnut data={chartData} options={options} />
        </div>
      )}
    </section>
  );
};

export default BreedChart;
